import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Avatar } from '@/components/Chips'
import { Icon } from '@/components/Icon'
import {
  documentUrl,
  fetchDocumentComments,
  postDocumentComment,
  type DocumentComment,
  type ProjectDocument,
} from '@/features/documents/api'
import { formatSize } from '@/features/documents/size'
import type { ApiError } from '@/lib/api/client'
import type { Visibility } from '@/types/domain'

/**
 * One document, opened over the library.
 *
 * The conversation is the point of the panel. Most of what gets said about a
 * file is said somewhere else entirely — a chat thread, a reply to the email it
 * arrived in — and then the next person to open it has no idea that clause 4 is
 * wrong. Here the argument is attached to the thing being argued about.
 *
 * The composer's visibility control is deliberately loud, for the same reason
 * it is on a work item: this is the likeliest place in the product for an
 * accidental disclosure.
 */
export function DocumentPanel({
  document: file,
  projectKey,
  onClose,
}: {
  document: ProjectDocument
  projectKey: string
  onClose: () => void
}) {
  const { t, i18n } = useTranslation()

  const comments = useQuery<DocumentComment[], ApiError>({
    queryKey: ['document-comments', file.id],
    queryFn: () => fetchDocumentComments(file.id),
    retry: false,
  })

  const download = useMutation<string, ApiError, void>({
    mutationFn: () => documentUrl(file.id),
    onSuccess: (url) => window.open(url, '_blank', 'noopener'),
  })

  return (
    <>
      <button
        type="button"
        aria-label={t('view.close')}
        onClick={onClose}
        className="fixed inset-0 z-40 bg-black/20"
      />

      <aside className="border-line bg-surface fixed inset-y-0 end-0 z-50 flex w-full max-w-xl flex-col border-s shadow-2xl">
        <header className="border-line flex items-start gap-3 border-b px-5 py-4">
          <Icon name="file" className="text-ink-500 mt-1 size-5 shrink-0" />
          <div className="min-w-0 flex-1">
            <h2 className="text-ink-900 text-lg leading-snug font-semibold break-words">
              {file.name}
            </h2>
            <p className="text-ink-500 mt-0.5 font-mono text-[11px] break-all">{file.fileName}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            title={t('view.close')}
            className="text-ink-500 hover:bg-surface-2 hover:text-ink-900 rounded p-1.5"
          >
            <Icon name="close" className="size-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <dl className="grid grid-cols-[110px_minmax(0,1fr)] items-center gap-y-2.5 text-[13px]">
            <dt className="text-ink-500">{t('document.added')}</dt>
            <dd className="text-ink-700">
              {new Date(file.createdAt).toLocaleDateString(i18n.language)}
            </dd>

            <dt className="text-ink-500">{t('document.uploadedBy')}</dt>
            <dd className="flex items-center gap-2">
              <Avatar name={file.uploadedBy?.name ?? null} color={file.uploadedBy?.color} size="sm" />
              <span className="text-ink-700">{file.uploadedBy?.name ?? '—'}</span>
            </dd>

            <dt className="text-ink-500">{t('document.size')}</dt>
            <dd className="text-ink-700 font-mono text-[12px]">
              {formatSize(file.sizeBytes, i18n.language)}
            </dd>

            <dt className="text-ink-500">{t('document.visibility')}</dt>
            <dd>
              <VisibilityBadge visibility={file.visibility} />
            </dd>
          </dl>

          <button
            type="button"
            onClick={() => download.mutate()}
            disabled={download.isPending}
            className="border-line text-ink-700 hover:bg-surface-2 mt-4 flex items-center gap-1.5 rounded border px-3 py-1.5 text-[12px] disabled:opacity-40"
          >
            <Icon name="download" className="size-3.5" />
            {t('document.download')}
          </button>

          {download.isError ? (
            <p className="mt-2 text-[12px] text-[color:var(--color-state-blocked)]">
              {t('document.downloadFailed')}
            </p>
          ) : null}

          <section className="border-line mt-5 border-t pt-4">
            <h3 className="text-ink-900 mb-3 text-[13px] font-semibold">{t('document.comments')}</h3>

            {comments.data === undefined ? (
              <p className="text-ink-500 font-mono text-[11px]">{t('common.loading')}</p>
            ) : comments.data.length === 0 ? (
              <p className="text-ink-500 text-[13px]">{t('document.noComments')}</p>
            ) : (
              <ul className="flex flex-col gap-3">
                {comments.data.map((comment) => (
                  <li key={comment.id} className="flex gap-2.5">
                    <Avatar
                      name={comment.author?.name ?? null}
                      color={comment.author?.color}
                      size="sm"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-baseline gap-2">
                        <span className="text-ink-900 text-[12px] font-medium">
                          {comment.author?.name ?? '—'}
                        </span>
                        <span className="text-ink-500 font-mono text-[10px]">
                          {comment.createdAt?.slice(0, 10) ?? ''}
                        </span>
                        <VisibilityBadge visibility={comment.visibility} />
                      </div>
                      <p className="text-ink-700 mt-0.5 text-[13px] whitespace-pre-line">
                        {comment.body}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <Composer documentId={file.id} projectKey={projectKey} />
      </aside>
    </>
  )
}

function VisibilityBadge({ visibility }: { visibility: Visibility }) {
  const { t } = useTranslation()

  return (
    <span
      className={`rounded px-1.5 py-px font-mono text-[9px] tracking-wide uppercase ${
        visibility === 'client' ? 'bg-brand-50 text-brand-700' : 'bg-surface-2 text-ink-500'
      }`}
    >
      {visibility === 'client' ? t('view.clientVisible') : t('view.internal')}
    </span>
  )
}

function Composer({ documentId, projectKey }: { documentId: string; projectKey: string }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [body, setBody] = useState('')
  const [visibility, setVisibility] = useState<Visibility>('internal')

  const send = useMutation<DocumentComment, ApiError, void>({
    mutationFn: () => postDocumentComment(documentId, { body: body.trim(), visibility }),
    onSuccess: () => {
      setBody('')
      void queryClient.invalidateQueries({ queryKey: ['document-comments', documentId] })
      // The library shows a count per document, so it is stale the moment a
      // comment lands.
      void queryClient.invalidateQueries({ queryKey: ['documents', projectKey] })
    },
  })

  return (
    <form
      className="border-line bg-surface-2 border-t px-5 py-3"
      onSubmit={(event) => {
        event.preventDefault()
        if (body.trim().length > 0) send.mutate()
      }}
    >
      <div className="mb-2 flex items-center gap-1">
        {(['internal', 'client'] as const).map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => setVisibility(option)}
            aria-pressed={visibility === option}
            className={`rounded px-2 py-0.5 font-mono text-[10px] tracking-wide uppercase ${
              visibility === option
                ? option === 'client'
                  ? 'bg-brand-600 text-white'
                  : 'bg-ink-900 text-white'
                : 'text-ink-500 hover:bg-surface'
            }`}
          >
            {option === 'client' ? t('view.clientVisible') : t('view.internal')}
          </button>
        ))}
      </div>

      <textarea
        value={body}
        onChange={(event) => setBody(event.target.value)}
        placeholder={t('document.addComment')}
        rows={2}
        className="border-line bg-surface w-full resize-none rounded border px-2.5 py-1.5 text-[13px] outline-none focus:border-[color:var(--color-brand-300)]"
      />

      <div className="mt-1.5 flex items-center gap-3">
        <p className="text-ink-500 min-w-0 flex-1 text-[10px] leading-tight">
          {t('view.visibilityNote')}
        </p>
        <button
          type="submit"
          disabled={send.isPending || body.trim().length === 0}
          className="bg-brand-600 shrink-0 rounded px-3 py-1.5 text-[12px] font-medium text-white disabled:opacity-40"
        >
          {t('view.send')}
        </button>
      </div>
    </form>
  )
}
